import * as THREE from 'three';
import Engine from './Engine';
import {GroupsManager} from './GroupsManager';
import Callout from './Callout';
import ARTracker from "./ARTracker";
import MaterialAsset from './MaterialAsset';
import ProjectSaver from './ProjectSaver';
import {assert} from './utilities/ErrorHandling';
import MathUtils from './utilities/MathUtils';
import SceneObjectUtils from './utilities/SceneObjectUtils';
import TransformUtils from './utilities/TransformUtils';
import {MetadataManager} from './MetadataManager';
import {VisibilityModes} from "../interfaces/enums";
import SetObjectParentAction from '../actions/SetObjectParentAction';
import {CustomEventTypes, defaultPartsCatalogObject, RenderModes, SaveModificationTypes} from '../globals';
import {Trigger} from '../interfaces/Trigger';
import {IVector3} from '../interfaces/Misc';

type LinkedSceneToObject = Map<string, THREE.Object3D>;

export default class DefaultSceneManager
{
	private defaultObjectAndSceneToLinkedObject: Map<string, LinkedSceneToObject> = new Map();
	private defaultSceneGuidToLinkedScenesMap: Map<string, THREE.Scene[]> = new Map();
	private linkedObjectGuidToDefaultObjectMap: Map<string, THREE.Object3D> = new Map();
	private defaultObjectsCopyMap: Map<THREE.Object3D, THREE.Object3D> = new Map();
	private static instance: DefaultSceneManager;

	static getInstance()
	{
		if (!DefaultSceneManager.instance)
		{
			DefaultSceneManager.instance = new DefaultSceneManager();
		}
		return DefaultSceneManager.instance;
	}

	setDefaultScene(scene: THREE.Scene, copiedFrom: THREE.Scene | null = null): void
	{
		scene.userData.isDefaultScene = true;
		this.defaultSceneGuidToLinkedScenesMap.set(scene.userData.guid, []);

		const sceneRoot: THREE.Object3D = Engine.getInstance().getSelectionRoot(scene.userData.guid);
		sceneRoot.traverse((child) =>
		{
			this.defaultObjectAndSceneToLinkedObject.set(child.userData.guid, new Map<string, THREE.Object3D>());
		});

		if (copiedFrom)
		{
			this.defaultObjectsCopyMap.clear();
			const sourceSceneRoot = Engine.getInstance().getSelectionRoot(copiedFrom.userData.guid);
			SceneObjectUtils.parallelTraverse(sceneRoot, sourceSceneRoot, (sceneChild, sourceSceneChild) =>
			{
				this.defaultObjectsCopyMap.set(sourceSceneChild, sceneChild);
			});
		}
	}

	onDefaultSceneLoaded(scene: THREE.Scene): void
	{
		scene.userData.isDefaultScene = true;
		this.defaultSceneGuidToLinkedScenesMap.set(scene.userData.guid, []);
		this.onDefaultObjectLoaded(Engine.getInstance().getSelectionRoot(scene.userData.guid));
	}

	onDefaultObjectLoaded(object: THREE.Object3D): void
	{
		object.traverse((child) =>
		{
			if (child.userData.guid)
			{
				this.defaultObjectAndSceneToLinkedObject.set(child.userData.guid, new Map<string, THREE.Object3D>());
			}
		});
	}

	onLinkedSceneLoaded(scene: THREE.Scene, defaultSceneGuid: string): void
	{
		scene.userData.defaultSceneGuid = defaultSceneGuid;
		assert(this.defaultSceneGuidToLinkedScenesMap.has(defaultSceneGuid));
		this.defaultSceneGuidToLinkedScenesMap.get(defaultSceneGuid)?.push(scene);
	}

	onLinkedObjectLoaded(object: THREE.Object3D, sceneGuid: string, defaultObject: THREE.Object3D): void
	{
		SceneObjectUtils.parallelTraverse(object, defaultObject, (traversedLinkedObject: THREE.Object3D, traversedDefaultObject: THREE.Object3D) =>
		{
			if (traversedLinkedObject.userData.guid)
			{
				assert(!this.defaultObjectAndSceneToLinkedObject.get(traversedDefaultObject.userData.guid)?.has(sceneGuid));
				this.linkedObjectGuidToDefaultObjectMap.set(traversedLinkedObject.userData.guid, traversedDefaultObject);
				this.defaultObjectAndSceneToLinkedObject.get(traversedDefaultObject.userData.guid)?.set(sceneGuid, traversedLinkedObject);
				traversedLinkedObject.userData.defaultObjectGuid = traversedDefaultObject.userData.guid;
			}
		});
	}

	onLoadedObjectDisposed(object: THREE.Object3D): void
	{
		const defaultObject = this.getDefaultObject(object.userData.guid);
		if (defaultObject)
		{
			SceneObjectUtils.parallelTraverse(object, defaultObject, (traversedLinkedObject, traversedDefaultObject) =>
			{
				if (traversedLinkedObject.userData.guid)
				{
					const linkedObjects = this.getLinkedObjects(traversedDefaultObject);
					if (linkedObjects.includes(traversedLinkedObject)) linkedObjects.splice(linkedObjects.indexOf(traversedLinkedObject), 1);
					if (this.linkedObjectGuidToDefaultObjectMap.has(traversedLinkedObject.userData.guid)) this.linkedObjectGuidToDefaultObjectMap.delete(traversedLinkedObject.userData.guid);

					for (const [sceneGuid, object] of this.defaultObjectAndSceneToLinkedObject.get(traversedDefaultObject.userData.guid)?.entries() || [])
					{
						if (object === traversedLinkedObject)
						{
							this.defaultObjectAndSceneToLinkedObject.get(traversedDefaultObject.userData.guid)?.delete(sceneGuid);
							break;
						}
					}
				}
			});
		}
	}

	onSceneCopy(newScene: THREE.Scene, sourceScene: THREE.Scene, defaultScene: THREE.Scene): void
	{
		assert(defaultScene.userData.isDefaultScene);
		assert(this.defaultSceneGuidToLinkedScenesMap.has(defaultScene.userData.guid));
		this.defaultSceneGuidToLinkedScenesMap.get(defaultScene.userData.guid)?.push(newScene);
		newScene.userData.defaultSceneGuid = defaultScene.userData.guid;
		const sceneRoot = Engine.getInstance().getSelectionRoot(newScene.userData.guid);
		const sourceSceneRoot = Engine.getInstance().getSelectionRoot(sourceScene.userData.guid);

		if (sourceScene === defaultScene)
		{
			SceneObjectUtils.parallelTraverse(sceneRoot, sourceSceneRoot, (sceneChild, sourceSceneChild) =>
			{
				this.linkObject(sceneChild, sourceSceneChild, newScene);
			});
		}
		else
		{
			const scenesUnderSameSequence = this.getLinkedScenes(defaultScene.userData.guid).includes(sourceScene);
			if (scenesUnderSameSequence)
			{
				SceneObjectUtils.parallelTraverse(sceneRoot, sourceSceneRoot, (sceneChild, sourceSceneChild) =>
				{
					const defaultObject = this.getDefaultObject(sourceSceneChild.userData.guid);
					if (defaultObject)
					{
						this.linkObject(sceneChild, defaultObject, newScene);
					}
				});
			}
			else //either an entire sequence is being copied and new scenes need to be linked to the default scene of the newly created sequence
			//or a step from one sequence is being pasted in another, in which case we only link the scene selection roots
			{
				SceneObjectUtils.parallelTraverse(sceneRoot, sourceSceneRoot, (sceneChild, sourceSceneChild) =>
				{
					const sourceDefaultObject = this.getDefaultObject(sourceSceneChild.userData.guid);
					if (sourceDefaultObject)
					{
						const parallalDefaultObject = this.defaultObjectsCopyMap.get(sourceDefaultObject);
						if (parallalDefaultObject)
						{
							this.linkObject(sceneChild, parallalDefaultObject, newScene);
						}
					}
				});

				// link the root of the new scene to the root of the default scene
				const defaultSceneRoot = Engine.getInstance().getSelectionRoot(defaultScene.userData.guid);
				this.linkObject(sceneRoot, defaultSceneRoot, newScene);
			}
		}
	}

	onSceneMoved(scene: THREE.Scene, newDefaultScene: THREE.Scene, reversible = true): void
	{
		//sometimes CPS can travel across space and time to a different sequence - we want to unlink it from the previous default state and link it to a new one.
		//we do not want to delete any of linked objects currently contained within the CPS (from the old default state), we just want to unlink them.
		//following the same princinpal - we will not be spawning any existing default objects from the new default state

		if (scene.userData.defaultSceneGuid === newDefaultScene.userData.guid) return;

		const oldDefaultSceneGuid = scene.userData.defaultSceneGuid;
		const newDefaultSceneGuid = newDefaultScene.userData.guid;
		const sceneRoot: THREE.Object3D = Engine.getInstance().getSelectionRoot(scene.userData.guid);
		sceneRoot.traverse((child) =>
		{
			const defaultObject = this.getDefaultObject(child.userData.guid);
			if (defaultObject)
			{
				if (reversible) child.userData.premoveDefaultObjectGuid = defaultObject.userData.guid;

				this.linkedObjectGuidToDefaultObjectMap.delete(child.userData.guid);
				this.defaultObjectAndSceneToLinkedObject.get(defaultObject.userData.guid)?.delete(scene.userData.guid);
				delete child.userData.defaultObjectGuid;
			}
			else if (child.userData.premoveDefaultObjectGuid)
			{
				const unlinkedDefaultObject = this.findDefaultObject(child.userData.premoveDefaultObjectGuid, newDefaultSceneGuid);
				if (unlinkedDefaultObject)
				{
					this.linkObject(child, unlinkedDefaultObject, scene);
				}
			}
		});

		if (this.defaultSceneGuidToLinkedScenesMap.has(oldDefaultSceneGuid))
		{
			const index = this.defaultSceneGuidToLinkedScenesMap.get(oldDefaultSceneGuid)?.indexOf(scene);
			if (index !== undefined && index >= 0) this.defaultSceneGuidToLinkedScenesMap.get(oldDefaultSceneGuid)?.splice(index, 1);
		}

		scene.userData.defaultSceneGuid = newDefaultSceneGuid;
		assert(this.defaultSceneGuidToLinkedScenesMap.has(newDefaultSceneGuid));
		this.defaultSceneGuidToLinkedScenesMap.get(newDefaultSceneGuid)?.push(scene);

		const defaultSceneRoot = Engine.getInstance().getSelectionRoot(newDefaultSceneGuid);
		this.linkObject(sceneRoot, defaultSceneRoot, scene);
	}

	linkObject(object: THREE.Object3D, originalObject: THREE.Object3D, linkedScene: THREE.Scene): void
	{
		if (!object.userData.guid) return;

		const defaultObject = this.isDefaultObject(originalObject.userData.guid) ? originalObject : this.getDefaultObject(originalObject.userData.guid)!;
		assert(defaultObject);
		assert(!this.isDefaultObject(object.userData.guid));

		this.linkedObjectGuidToDefaultObjectMap.set(object.userData.guid, defaultObject);
		this.defaultObjectAndSceneToLinkedObject.get(defaultObject.userData.guid)?.set(linkedScene.userData.guid, object);
		object.userData.defaultObjectGuid = defaultObject.userData.guid;
	}

	breakLink(linkedObject: THREE.Object3D): void
	{
		const defaultObject = this.getDefaultObject(linkedObject.userData.guid);
		if (!defaultObject) return;

		//find the scene in which our linkedObject is in
		let linkedObjectSceneGuid = "";
		for (const [sceneGuid, object] of this.defaultObjectAndSceneToLinkedObject.get(defaultObject.userData.guid)?.entries() || [])
		{
			if (object === linkedObject)
			{
				linkedObjectSceneGuid = sceneGuid;
				break;
			}
		}

		linkedObject.userData.unlinkedDefaultObjectGuid = defaultObject.userData.guid;
		ProjectSaver.getInstance().markModifiedObject(defaultObject, SaveModificationTypes.Reference);
		linkedObject.traverse((child) =>
		{
			if (child.userData.guid)
			{
				const defaultObject = this.getDefaultObject(child.userData.guid);
				if (defaultObject)
				{
					this.linkedObjectGuidToDefaultObjectMap.delete(child.userData.guid);
					this.defaultObjectAndSceneToLinkedObject.get(defaultObject.userData.guid)?.delete(linkedObjectSceneGuid);
					delete child.userData.defaultObjectGuid;
				}
			}
		});
	}

	onDefaultObjectAdded(object: THREE.Object3D, sceneGuid: string): void
	{
		if (object instanceof ARTracker) return;
		const firstAddition = !this.defaultObjectAndSceneToLinkedObject.has(object.userData.guid);
		const linkedScenes = this.getLinkedScenes(sceneGuid);

		if (firstAddition)
		{
			object.traverse((child) =>
			{
				if (child.userData.guid)
				{
					this.defaultObjectAndSceneToLinkedObject.set(child.userData.guid, new Map<string, THREE.Object3D>());
				}
			});

			object.updateWorldMatrix(true, false);
			for (const linkedScene of linkedScenes)
			{
				const newParent = this.getLinkedObject(object.parent!, linkedScene.userData.guid);
				if (newParent && !newParent.userData.removedFromScene)
				{
					const newObject = SceneObjectUtils.cloneObject(object);
					object.matrixWorld.decompose(newObject.position, newObject.quaternion, newObject.scale);
					if (object instanceof THREE.Sprite) newObject.scale.copy(object.scale);
					Engine.getInstance().addObject(newObject, newParent, linkedScene.userData.guid, true);
					if (newObject.userData.triggers)
					{
						for (const trigger of newObject.userData.triggers)
						{
							trigger.defaultSyncedTrigger = true;
						}
					}

					SceneObjectUtils.parallelTraverse(newObject, object, (newObjectChild, defaultObjectChild) =>
					{
						this.linkObject(newObjectChild, defaultObjectChild, linkedScene);
					});
				}
			}
		}
		else
		{
			for (const linkedScene of linkedScenes)
			{
				const newParent = this.getLinkedObject(object.parent!, linkedScene.userData.guid);
				if (newParent && !newParent.userData.removedFromScene)
				{
					const newObject = this.getLinkedObject(object, linkedScene.userData.guid);
					if (newObject)
					{
						Engine.getInstance().addObject(newObject, newParent, linkedScene.userData.guid, true);
					}
				}
			}
		}
	}

	selectParallalObjects(selectedObjects: THREE.Object3D[], newSceneId: string): void
	{
		if (selectedObjects.length > 0)
		{
			Engine.getInstance().getPickingManager().clearSelection(false);
			const isDefaultScene = this.defaultSceneGuidToLinkedScenesMap.has(newSceneId);
			for (const object of selectedObjects)
			{
				const defaultObject = this.isDefaultObject(object.userData.guid) ? object : this.getDefaultObject(object.userData.guid);
				if (defaultObject)
				{
					if (isDefaultScene)
					{
						Engine.getInstance().getPickingManager().selectObject(defaultObject);
					}
					else
					{
						const parallalObject = this.defaultObjectAndSceneToLinkedObject.get(defaultObject.userData.guid)?.get(newSceneId);
						if (parallalObject && !parallalObject.userData.removedFromScene)
						{
							Engine.getInstance().getPickingManager().selectObject(parallalObject);
						}
					}
				}
			}
			Engine.getInstance().getPickingManager().storeSelection();
			document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged));
		}
	}

	syncObjectRemoval(object: THREE.Object3D): void
	{
		if (this.isDefaultObject(object.userData.guid))
		{
			for (const [sceneGuid, linkedObject] of this.defaultObjectAndSceneToLinkedObject.get(object.userData.guid)?.entries() || [])
			{
				if (!linkedObject.userData.removedFromScene)
				{
					ProjectSaver.getInstance().markModifiedObject(linkedObject, SaveModificationTypes.Removed);
					Engine.getInstance().removeObject(linkedObject, false, sceneGuid);
				}
			}
		}
	}

	syncGroupChange(defaultObjects: THREE.Object3D[], groupGuid: string, isAdding: boolean): void
	{
		for (const defaultObject of defaultObjects)
		{
			assert(this.isDefaultObject(defaultObject.userData.guid));
			for (const [sceneGuid, object] of this.defaultObjectAndSceneToLinkedObject.get(defaultObject.userData.guid)?.entries() || [])
			{
				if (isAdding)
				{
					GroupsManager.addToGroup(groupGuid, [object], sceneGuid);
				}
				else
				{
					GroupsManager.removeFromGroup(groupGuid, [object], sceneGuid);
				}
			}
		}
	}

	syncNativeObjectVectorProperty(defaultObjects: THREE.Object3D[], propertyName: string, newValues: any[]): void
	{
		for (let i = 0; i < defaultObjects.length; i++)
		{
			const defaultObject = defaultObjects[i];
			if (defaultObject instanceof ARTracker) continue;
			assert(this.isDefaultObject(defaultObject.userData.guid));
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				if (MathUtils.areVectorsEqual(linkedObject[propertyName], defaultObject[propertyName]))
				{
					linkedObject[propertyName].copy(newValues[i]);
				}
			}
		}
	}

	syncNativeObjectProperty(defaultObjects: THREE.Object3D[], propertyName: string, newValues: any[]): void
	{
		for (let i = 0; i < defaultObjects.length; i++)
		{
			const defaultObject = defaultObjects[i];
			if (defaultObject instanceof ARTracker) continue;
			assert(this.isDefaultObject(defaultObject.userData.guid));
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				if (linkedObject[propertyName] === defaultObject[propertyName])
				{
					linkedObject[propertyName] = newValues[i];
				}
			}
		}
	}

	syncCustomObjectProperty(defaultObjects: THREE.Object3D[], setterFunctionName: string, newValues: any[]): void
	{
		const getterFunctionName = setterFunctionName.replace('set', 'get');
		for (let i = 0; i < defaultObjects.length; i++)
		{
			const defaultObject = defaultObjects[i];
			if (defaultObject instanceof ARTracker) continue;
			assert(this.isDefaultObject(defaultObject.userData.guid));
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				if (linkedObject[getterFunctionName]() === defaultObject[getterFunctionName]())
				{
					linkedObject[setterFunctionName](newValues[i]);
				}
			}
		}
	}

	syncTranslateWithoutChildren(defaultObjects: THREE.Object3D[], newValues: THREE.Vector3[]): void
	{
		for (let i = 0; i < defaultObjects.length; i++)
		{
			const defaultObject = defaultObjects[i];
			assert(this.isDefaultObject(defaultObject.userData.guid));
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				if (MathUtils.areVectorsEqual(linkedObject.position, defaultObject.position))
				{
					const childrenInitialPositions: THREE.Vector3[] = [];
					for (const child of linkedObject.children)
					{
						if (!(child instanceof Callout))
						{
							const worldPosition = new THREE.Vector3();
							child.getWorldPosition(worldPosition);
							childrenInitialPositions.push(worldPosition);
						}
					}
					linkedObject.position.copy(newValues[i]);

					let childIndex = 0;
					for (const child of linkedObject.children)
					{
						if (!(child instanceof Callout))
						{
							TransformUtils.setWorldPosition(child, childrenInitialPositions[childIndex++]);
							ProjectSaver.getInstance().markModifiedObject(child, SaveModificationTypes.Position);
						}
					}
				}
			}
		}
	}

	syncCallout(defaultObjects: THREE.Object3D[], newValues: string[]): void
	{
		for (let i = 0; i < defaultObjects.length; i++)
		{
			const defaultObject = defaultObjects[i];
			assert(this.isDefaultObject(defaultObject.userData.guid));
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				if (Callout.getCallout(linkedObject.userData.guid))
				{
					Callout.removeCallout(linkedObject);
				}

				if (newValues[i])
				{
					linkedObject.userData.annotation = JSON.parse(newValues[i]);
					Callout.addCallout(linkedObject);
				}
			}
		}
	}

	syncCalloutProperty(defaultObjects: THREE.Object3D[], setterFunctionName: string, newValues: any[]): void
	{
		const getterFunctionName = setterFunctionName.replace('set', 'get');
		for (let i = 0; i < defaultObjects.length; i++)
		{
			const defaultObject = defaultObjects[i];
			assert(this.isDefaultObject(defaultObject.userData.guid));
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				const linkedCallout = Callout.getCallout(linkedObject.userData.guid);
				const defaultCallout = Callout.getCallout(defaultObject.userData.guid);
				if (linkedCallout && defaultCallout && JSON.stringify(linkedCallout[getterFunctionName]()) === JSON.stringify(defaultCallout[getterFunctionName]()))
				{
					linkedCallout[setterFunctionName](newValues[i]);
				}
			}
		}
	}

	syncCalloutEnabled(defaultObjects: THREE.Object3D[], enabledValues: boolean[]): void
	{
		for (let i = 0; i < defaultObjects.length; i++)
		{
			const defaultObject = defaultObjects[i];
			assert(this.isDefaultObject(defaultObject.userData.guid));
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				if (enabledValues[i])
				{
					Callout.addCallout(linkedObject);
				}
				else
				{
					Callout.removeCallout(linkedObject);
				}
			}
		}
	}

	syncBillboardProperty(defaultObjects: THREE.Object3D[], propertyName: string, newValues: any[]): void
	{
		for (let i = 0; i < defaultObjects.length; i++)
		{
			const defaultObject = defaultObjects[i];
			assert(this.isDefaultObject(defaultObject.userData.guid));
			if (propertyName === 'targetGuid')
			{
				const defaultTargetObjectGuid = newValues[i];
				for (const [linkedSceneGuid, linkedObject] of this.defaultObjectAndSceneToLinkedObject.get(defaultObject.userData.guid)?.entries() || [])
				{
					if (defaultTargetObjectGuid)
					{
						const linkedTargetObject = this.defaultObjectAndSceneToLinkedObject.get(defaultTargetObjectGuid)?.get(linkedSceneGuid);
						if (linkedTargetObject)
						{
							linkedObject.userData.billboard['targetGuid'] = linkedTargetObject.userData.guid;
							ProjectSaver.getInstance().markModifiedObject(linkedTargetObject, SaveModificationTypes.Reference);
						}
						else
						{
							linkedObject.userData.billboard['targetGuid'] = null;
						}
					}
					else
					{
						linkedObject.userData.billboard['targetGuid'] = null;
					}
				}
			}
			else
			{
				const linkedObjects = this.getLinkedObjects(defaultObject);
				for (const linkedObject of linkedObjects)
				{
					if (JSON.stringify(linkedObject.userData.billboard[propertyName]) === JSON.stringify(defaultObject.userData.billboard[propertyName]))
					{
						linkedObject.userData.billboard[propertyName] = JSON.parse(JSON.stringify(newValues[i]));
					}
				}
			}
		}
	}

	syncBillboardEnabled(defaultObjects: THREE.Object3D[], enabledValues: boolean[]): void
	{
		for (let i = 0; i < defaultObjects.length; i++)
		{
			const defaultObject = defaultObjects[i];
			assert(this.isDefaultObject(defaultObject.userData.guid));
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				const defaultEnabled = defaultObject.userData.billboard ? defaultObject.userData.billboard.enabled : false;
				const linkedEnabled = linkedObject.userData.billboard ? linkedObject.userData.billboard.enabled : false;

				if (defaultEnabled === linkedEnabled || (defaultEnabled && !linkedObject.userData.billboard))
				{
					let linkedObjectSceneGuid = "";
					let parent = linkedObject.parent;
					while (parent)
					{
						if (parent.type === 'Scene')
						{
							linkedObjectSceneGuid = parent.userData.guid;
							break;
						}
						parent = parent.parent;
					}

					assert(linkedObjectSceneGuid);
					if (enabledValues[i])
					{
						Engine.getInstance().getBillboardManager().enableBillboard(linkedObject, linkedObjectSceneGuid);
					}
					else
					{
						Engine.getInstance().getBillboardManager().disableBillboard(linkedObject, linkedObjectSceneGuid);
					}
				}
			}
		}
	}

	syncAddTrigger(defaultObjects: THREE.Object3D[], trigger: Trigger, triggerIndex = 0): void
	{
		for (const defaultObject of defaultObjects)
		{
			if (this.isDefaultObject(defaultObject.userData.guid))
			{
				for (const [sceneGuid, linkedObject] of this.defaultObjectAndSceneToLinkedObject.get(defaultObject.userData.guid)?.entries() || [])
				{
					if (!linkedObject.userData.triggers) linkedObject.userData.triggers = [];

					if (defaultObject.userData.triggers)
					{
						for (let i = 0; i < defaultObject.userData.triggers.length; i++)
						{
							if (!linkedObject.userData.triggers[i] || !linkedObject.userData.triggers[i].defaultSyncedTrigger)
							{
								//backwards comptability fix
								linkedObject.userData.triggers.splice(i, 0, JSON.parse(JSON.stringify(defaultObject.userData.triggers[i])));
								linkedObject.userData.triggers[i].defaultSyncedTrigger = true;
								linkedObject.userData.triggers[i].removedSyncedTrigger = true;
								linkedObject.userData.triggers.sort((a, b) =>
								{
									if (a.defaultSyncedTrigger) return -1;
									if (b.defaultSyncedTrigger) return 1;
									return 0;
								});
							}
						}
					}

					linkedObject.userData.triggers.splice(triggerIndex, 0, JSON.parse(JSON.stringify(trigger)));
					linkedObject.userData.triggers[triggerIndex].defaultSyncedTrigger = true;
					if (!linkedObject.userData.triggers[triggerIndex].targetObject)
					{
						linkedObject.userData.triggers[triggerIndex].targetObject = linkedObject.userData.guid;
					}
					else
					{
						const linkedTargetObject = this.getLinkedObject(linkedObject.userData.triggers[triggerIndex].targetObject, sceneGuid);
						if (linkedTargetObject)
						{
							ProjectSaver.getInstance().markModifiedObject(linkedTargetObject, SaveModificationTypes.Reference);
							linkedObject.userData.triggers[triggerIndex].targetObject = linkedTargetObject.userData.guid;
						}
						else
						{
							linkedObject.userData.triggers[triggerIndex].targetObject = "";
						}
					}
					linkedObject.userData.triggers.sort((a, b) =>
					{
						if (a.defaultSyncedTrigger) return -1;
						if (b.defaultSyncedTrigger) return 1;
						return 0;
					});
					ProjectSaver.getInstance().markModifiedObject(linkedObject, SaveModificationTypes.Trigger);
					ProjectSaver.getInstance().markModifiedObject(linkedObject, SaveModificationTypes.Reference);
				}
			}
		}
	}

	syncTriggerProperty(defaultObjects: THREE.Object3D[], triggerIndex: number, propertyName: string, values: any[]): void
	{
		for (let i = 0; i < defaultObjects.length; i++)
		{
			const defaultObject = defaultObjects[i];
			if (this.isDefaultObject(defaultObject.userData.guid))
			{
				for (const [sceneGuid, linkedObject] of this.defaultObjectAndSceneToLinkedObject.get(defaultObject.userData.guid)?.entries() || [])
				{
					//backwards comptability fix
					if (!linkedObject.userData.triggers[triggerIndex])
					{
						linkedObject.userData.triggers.splice(triggerIndex, 0, JSON.parse(JSON.stringify(defaultObject.userData.triggers[triggerIndex])));
						linkedObject.userData.triggers[triggerIndex].defaultSyncedTrigger = true;
						linkedObject.userData.triggers[triggerIndex].removedSyncedTrigger = true;
						linkedObject.userData.triggers.sort((a, b) =>
						{
							if (a.defaultSyncedTrigger) return -1;
							if (b.defaultSyncedTrigger) return 1;
							return 0;
						});
					}

					if (propertyName === 'targetObject')
					{
						const defaultTargetObjectGuid = values[i];
						const linkedTargetObject = this.getLinkedObject(defaultTargetObjectGuid, sceneGuid);
						if (linkedTargetObject)
						{
							ProjectSaver.getInstance().markModifiedObject(linkedTargetObject, SaveModificationTypes.Reference);
							linkedObject.userData.triggers[triggerIndex].targetObject = linkedTargetObject.userData.guid;
						}
						else
						{
							linkedObject.userData.triggers[triggerIndex].targetObject = "";
						}

					}
					else if (linkedObject.userData.triggers[triggerIndex][propertyName] === defaultObject.userData.triggers[triggerIndex][propertyName])
					{
						linkedObject.userData.triggers[triggerIndex][propertyName] = values[i];
						if (propertyName === 'onlineScenarioName')
						{
							linkedObject.userData.triggers[triggerIndex].scenarioId = defaultObject.userData.triggers[triggerIndex].scenarioId;
						}
					}
				}
			}

		}
	}

	syncRemoveTrigger(defaultObjects: THREE.Object3D[], triggerIndex = 0): void
	{
		for (const defaultObject of defaultObjects)
		{
			if (this.isDefaultObject(defaultObject.userData.guid))
			{
				const linkedObjects = this.getLinkedObjects(defaultObject);
				for (const linkedObject of linkedObjects)
				{
					linkedObject.userData.triggers.splice(triggerIndex, 1);
				}
			}
		}
	}

	syncRemoveAllTriggers(defaultObjects: THREE.Object3D[]): void
	{
		for (const defaultObject of defaultObjects)
		{
			if (this.isDefaultObject(defaultObject.userData.guid))
			{
				const linkedObjects = this.getLinkedObjects(defaultObject);
				for (const linkedObject of linkedObjects)
				{
					if (linkedObject.userData.triggers) linkedObject.userData.triggers.length = 0;
				}
			}
		}
	}

	syncPivot(defaultObject: THREE.Object3D, offset: IVector3): void
	{
		if (!this.isDefaultObject(defaultObject.userData.guid)) return;

		const linkedObjects = this.getLinkedObjects(defaultObject);
		for (const linkedObject of linkedObjects)
		{
			if (!linkedObject.userData.pivot) linkedObject.userData.pivot = JSON.parse(JSON.stringify(defaultObject.userData.pivot));

			if (JSON.stringify(linkedObject.userData.pivot.relativeOffset) === JSON.stringify(defaultObject.userData.pivot.relativeOffset))
			{
				if (linkedObject.userData.pivot.space === defaultObject.userData.pivot.space)
				{
					TransformUtils.applyPivot(linkedObject, offset, true);
				}
				else
				{
					const convertedOffset = TransformUtils.getConvertedPivotOffsetSpace(defaultObject, offset, linkedObject.userData.pivot.space);
					TransformUtils.applyPivot(linkedObject, convertedOffset, true);
				}
			}
		}
	}

	syncVisibilityMode(defaultObject: THREE.Object3D, visibilityMode: VisibilityModes, renderMode: RenderModes, customMaterial?: string): void
	{
		if (this.isDefaultObject(defaultObject.userData.guid))
		{
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				if (linkedObject.userData.visibility[renderMode] === defaultObject.userData.visibility[renderMode])
				{
					linkedObject.userData.visibility[renderMode] = visibilityMode;
					if (visibilityMode === VisibilityModes.Custom)
					{
						if (renderMode === RenderModes.Standalone)
						{
							if (linkedObject.userData.visibility.customStandaloneMaterial === defaultObject.userData.visibility.customStandaloneMaterial)
							{
								linkedObject.userData.visibility.customStandaloneMaterial = customMaterial;
							}
						}
						else if (renderMode === RenderModes.Device)
						{
							if (linkedObject.userData.visibility.customDeviceMaterial === defaultObject.userData.visibility.customDeviceMaterial)
							{
								linkedObject.userData.visibility.customDeviceMaterial = customMaterial;
							}
						}
					}
				}
			}
		}
	}

	syncVisibilityModeCustomMaterial(defaultObject: THREE.Object3D, renderMode: RenderModes, customMaterial: string | undefined): void
	{
		if (this.isDefaultObject(defaultObject.userData.guid))
		{
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				if (renderMode === RenderModes.Standalone)
				{
					if (linkedObject.userData.visibility.customStandaloneMaterial === defaultObject.userData.visibility.customStandaloneMaterial)
					{
						linkedObject.userData.visibility.customStandaloneMaterial = customMaterial;
					}
				}
				else if (renderMode === RenderModes.Device)
				{
					if (linkedObject.userData.visibility.customDeviceMaterial === defaultObject.userData.visibility.customDeviceMaterial)
					{
						linkedObject.userData.visibility.customDeviceMaterial = customMaterial;
					}
				}
			}
		}
	}

	syncVisibilityParentOverride(defaultObject: THREE.Object3D, override: boolean): void
	{
		if (this.isDefaultObject(defaultObject.userData.guid))
		{
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				if (linkedObject.userData.visibility.overrideParent === defaultObject.userData.visibility.overrideParent)
				{
					linkedObject.userData.visibility.overrideParent = override;
				}
			}
		}
	}

	syncChildReordering(reorderedChildren: THREE.Object3D[], atIndex: number): void
	{
		if (!this.isDefaultObject(reorderedChildren[0].userData.guid)) return;
		if (reorderedChildren[0].parent!.parent && reorderedChildren[0].parent!.parent.type === 'Scene') atIndex--; //compensating for trackerRoot

		const parentMap = new Map();
		for (const defaultObject of reorderedChildren)
		{
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				parentMap.set(linkedObject.userData.guid, linkedObject.parent);
				linkedObject.removeFromParent();
			}
		}

		for (let i = 0; i < reorderedChildren.length; i++)
		{
			const defaultObject = reorderedChildren[i];
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				if (linkedObject.userData.removedFromScene) continue;
				const parent = parentMap.get(linkedObject.userData.guid);
				assert(this.getDefaultObject(parent.userData.guid) === defaultObject.parent);
				parent.add(linkedObject);
				parent.children.splice(atIndex + i, 0, parent.children.splice(parent.children.length - 1, 1)[0]);
				ProjectSaver.getInstance().markModifiedObject(parent, SaveModificationTypes.ReorderedChildren);
			}
		}
	}

	syncParenting(childObject: THREE.Object3D, newParent: THREE.Object3D, atIndex: number): void
	{
		if (this.isDefaultObject(childObject.userData.guid))
		{
			if (newParent === Engine.getInstance().getSelectionRoot())
			{
				atIndex--; //when parent is the scene root, the index is offset by 1 due to the current scene containing the tracker root at index 0
			}
			const linkedObjects = this.getLinkedObjects(childObject);
			for (let i = linkedObjects.length - 1; i >= 0; i--)
			{
				const linkedObject = linkedObjects[i];
				if (linkedObject.userData.removedFromScene) continue;
				assert(this.getDefaultObject(linkedObject.parent!.userData.guid) === childObject.parent);

				//find the scene in which our linkedObject is in
				let linkedObjectSceneGuid = "";
				for (const [sceneGuid, object] of this.defaultObjectAndSceneToLinkedObject.get(childObject.userData.guid)?.entries() || [])
				{
					if (object === linkedObject)
					{
						linkedObjectSceneGuid = sceneGuid;
						break;
					}
				}

				assert(linkedObjectSceneGuid);
				const linkedNewParent = this.defaultObjectAndSceneToLinkedObject.get(newParent.userData.guid)?.get(linkedObjectSceneGuid);
				if (linkedNewParent && !linkedNewParent.userData.removedFromScene)
				{
					ProjectSaver.getInstance().onReparent(linkedObject, linkedNewParent);
					linkedNewParent.attach(linkedObject);
					linkedNewParent.children.splice(atIndex, 0, linkedNewParent.children.splice(linkedNewParent.children.length - 1, 1)[0]);
				}
				else
				{
					this.breakLink(linkedObject); //default object gets reparented under an object who has no linked-parallal in this scene
				}
			}
		}
		else
		{
			const defaultObject = this.getDefaultObject(childObject.userData.guid);
			if (defaultObject)
			{
				//if we are changing the parent of a linked object - we must break its link to its default object
				this.breakLink(childObject);
			}
			else if (childObject.userData.unlinkedDefaultObjectGuid)
			{
				//if we are changing parent of an unlinked object - we must check if it was previously linked, and if the link should be restored
				const unlinkedDefaultObject = this.findDefaultObject(childObject.userData.unlinkedDefaultObjectGuid);
				if (unlinkedDefaultObject)
				{
					const unlinkedDefaultParent = unlinkedDefaultObject.parent;
					const newParentDefault = this.getDefaultObject(newParent.userData.guid);
					if (unlinkedDefaultParent === newParentDefault)
					{
						//find which scene newParent is in
						let newParentScene: THREE.Scene | null = null;
						for (const [sceneGuid, object] of this.defaultObjectAndSceneToLinkedObject.get(newParentDefault!.userData.guid)?.entries() || [])
						{
							if (object === newParent)
							{
								newParentScene = Engine.getInstance().getScene(sceneGuid);
								break;
							}
						}
						assert(newParentScene);
						this.linkObject(childObject, unlinkedDefaultObject, newParentScene!);
						delete childObject.userData.unlinkedDefaultObjectGuid;

						const areObjectsMatching = (a: THREE.Object3D, b: THREE.Object3D) =>
						{
							if (a.userData.originalUuid === b.userData.originalUuid) return true;
							if (a.constructor !== b.constructor) return false;
							if (a.type !== b.type) return false;
							const aHasGeometry = (a as THREE.Mesh).geometry !== undefined;
							const bHasGeometry = (b as THREE.Mesh) !== undefined;
							if (aHasGeometry !== bHasGeometry) return false;
							if (aHasGeometry) return (a as THREE.Mesh).geometry.uuid === (b as THREE.Mesh).geometry.uuid;

							return a.name === b.name;
						}

						const syncTriggerState = (linkedObject: THREE.Object3D, defaultObject: THREE.Object3D) =>
						{
							if (!linkedObject.userData.triggers?.length) return;
							const numDefaultTriggers = defaultObject.userData.triggers ? defaultObject.userData.triggers.length : 0;
							let defaultSyncedTriggerTriggersCounter = 0;
							for (const trigger of linkedObject.userData.triggers)
							{
								if (trigger.defaultSyncedTrigger) defaultSyncedTriggerTriggersCounter++;
							}

							if (defaultSyncedTriggerTriggersCounter > numDefaultTriggers)
							{
								const numExcessTriggers = defaultSyncedTriggerTriggersCounter - numDefaultTriggers;
								linkedObject.userData.triggers.sort((a: Trigger, b: Trigger) =>
								{
									if (a.removedSyncedTrigger) return -1;
									if (b.removedSyncedTrigger) return 1;
									return 0;
								});

								for (let i = 0; i < numExcessTriggers; i++)
								{
									if (linkedObject.userData.triggers[i].removedSyncedTrigger)
									{
										linkedObject.userData.triggers[i] = null;
									}
									else
									{
										delete linkedObject.userData.triggers[i].defaultSyncedTrigger;
									}
								}

								linkedObject.userData.triggers = linkedObject.userData.triggers.filter((trigger) => { return trigger != null; });
							}
						}

						const relinkChildren = (root: THREE.Object3D, defaultRoot: THREE.Object3D) =>
						{
							syncTriggerState(root, defaultRoot);

							for (let i = 0; i < root.children.length; i++)
							{
								const unlinkedChild = root.children[i];
								if (!unlinkedChild.userData.guid) continue;
								if (defaultRoot.children.length >= i + 1)
								{
									const potentiallyMatchingDefaultObject = defaultRoot.children[i];
									const existingLinkedObject = this.getLinkedObject(potentiallyMatchingDefaultObject.userData.guid, newParentScene!.userData.guid);
									if (!existingLinkedObject && areObjectsMatching(unlinkedChild, potentiallyMatchingDefaultObject))
									{
										this.linkObject(unlinkedChild, potentiallyMatchingDefaultObject, newParentScene!);
										relinkChildren(unlinkedChild, potentiallyMatchingDefaultObject);
										continue;
									}
								}

								for (let j = 0; j < defaultRoot.children.length; j++)
								{
									if (j === i) continue;
									const potentiallyMatchingDefaultObject = defaultRoot.children[j];
									const existingLinkedObject = this.getLinkedObject(potentiallyMatchingDefaultObject.userData.guid, newParentScene!.userData.guid);
									if (!existingLinkedObject && areObjectsMatching(unlinkedChild, potentiallyMatchingDefaultObject))
									{
										this.linkObject(unlinkedChild, potentiallyMatchingDefaultObject, newParentScene!);
										relinkChildren(unlinkedChild, potentiallyMatchingDefaultObject);
										break;
									}
								}
							}
						}

						relinkChildren(childObject, unlinkedDefaultObject);
					}
				}
			}
		}
	}

	syncMaterialAsset(defaultObject: THREE.Object3D, materialAsset: MaterialAsset): void
	{
		assert(this.isDefaultObject(defaultObject.userData.guid));
		const linkedObjects = this.getLinkedObjects(defaultObject);
		for (const linkedObject of linkedObjects)
		{
			if (MaterialAsset.getMaterialAssetByObjectGuid(defaultObject.userData.guid)?.guid ===
				MaterialAsset.getMaterialAssetByObjectGuid(linkedObject.userData.guid)?.guid)
			{
				MaterialAsset.assignMaterialAssetToObject(materialAsset, linkedObject as THREE.Mesh);
			}
		}
	}

	syncAddMetadataKey(defaultObjects: THREE.Object3D[], key: string): void
	{
		for (const defaultObject of defaultObjects)
		{
			assert(this.isDefaultObject(defaultObject.userData.guid));
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				MetadataManager.addCustomKey(linkedObject, key);
			}
		}
	}

	syncRemoveMetadataKey(defaultObjects: THREE.Object3D[], key: string): void
	{
		for (const defaultObject of defaultObjects)
		{
			assert(this.isDefaultObject(defaultObject.userData.guid));
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				MetadataManager.removeCustomKey(linkedObject, key);
			}
		}
	}

	syncMedataValue(defaultObjects: THREE.Object3D[], key: string, values: string[]): void
	{
		for (let i = 0; i < defaultObjects.length; i++)
		{
			const defaultObject = defaultObjects[i];
			assert(this.isDefaultObject(defaultObject.userData.guid));
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				if (linkedObject.userData.metaData && linkedObject.userData.metaData[key] &&
					linkedObject.userData.metaData[key].value === defaultObject.userData.metaData[key].value)
				{
					linkedObject.userData.metaData[key].value = values[i];
				}
			}
		}
	}

	syncPartsCatalogValue(defaultObjects: THREE.Object3D[], key: string, values: any[])
	{
		for (let i = 0; i < defaultObjects.length; i++)
		{
			const defaultObject = defaultObjects[i];
			assert(this.isDefaultObject(defaultObject.userData.guid));
			const linkedObjects = this.getLinkedObjects(defaultObject);
			for (const linkedObject of linkedObjects)
			{
				if (!linkedObject.userData.partsCatalog) linkedObject.userData.partsCatalog = { ...defaultPartsCatalogObject };
				const defaultObjectValues = { ...defaultObject.userData.partsCatalog ?? defaultPartsCatalogObject };
				if (linkedObject.userData.partsCatalog[key] === defaultObjectValues[key])
				{
					linkedObject.userData.partsCatalog[key] = values[i];
				}
			}
		}
	}

	onObjectAssetSwap(oldDefaultObject: THREE.Object3D | null, oldLinkedObjects: (THREE.Object3D | null)[], newDefaultObject: THREE.Object3D | null, newLinkedObjects: (THREE.Object3D | null)[], linkedScenes: THREE.Scene[], oldAssetId: string, newAssetId: string)
	{
		assert(!oldDefaultObject || this.isDefaultObject(oldDefaultObject.userData.guid));
		assert(!newDefaultObject || (!oldDefaultObject || this.isDefaultObject(newDefaultObject.userData.guid)));

		const traverseClearDefaultObjectData = (object: THREE.Object3D, rootObject: THREE.Object3D) =>
		{
			if (object.userData.assetId !== oldAssetId || !object.userData.guid || (object.userData.markedSaveRoot && object !== rootObject)) return;

			this.defaultObjectAndSceneToLinkedObject.delete(object.userData.guid);

			for (const child of object.children)
			{
				traverseClearDefaultObjectData(child, rootObject);
			}
		}

		const traverseClearLinkedObjectData = (object: THREE.Object3D, rootObject: THREE.Object3D) =>
		{
			if (object.userData.assetId !== oldAssetId || !object.userData.guid || (object.userData.markedSaveRoot && object !== rootObject)) return;

			this.linkedObjectGuidToDefaultObjectMap.delete(object.userData.guid);

			for (const child of object.children)
			{
				traverseClearLinkedObjectData(child, rootObject);
			}
		}

		const traverseBuildDefaultObjectData = (object: THREE.Object3D, rootObject: THREE.Object3D) =>
		{
			if (object.userData.assetId !== newAssetId || !object.userData.guid || (object.userData.markedSaveRoot && object !== rootObject)) return;

			this.defaultObjectAndSceneToLinkedObject.set(object.userData.guid, new Map<string, THREE.Object3D>());

			for (const child of object.children)
			{
				traverseBuildDefaultObjectData(child, rootObject);
			}
		}

		const paralleltraverseBuildLinkedObjectData = (linkedObject: THREE.Object3D, defaultObject: THREE.Object3D, rootObject: THREE.Object3D, linkedScene: THREE.Scene) =>
		{
			this.linkObject(linkedObject, defaultObject, linkedScene);

			const linkedChildrenCopy = [...linkedObject.children];
			for (let i = 0; i < defaultObject.children.length; i++)
			{
				//for each default object child, find a corresponding linked object, if one is found - link and continue traversing:
				const defaultChild = defaultObject.children[i];
				for (let j = linkedChildrenCopy.length - 1; j >= 0; j--)
				{
					const linkedChild = linkedChildrenCopy[j];
					if (linkedChild.userData.guid && !linkedChild.userData.markedSaveRoot &&
						linkedChild.userData.assetId === newAssetId &&
						linkedChild.userData.originalUuid === defaultChild.userData.originalUuid)
					{
						paralleltraverseBuildLinkedObjectData(linkedChild, defaultChild, rootObject, linkedScene);
						linkedChildrenCopy.splice(j, 1);
						break;
					}
				}
			}
		}

		if (oldDefaultObject) traverseClearDefaultObjectData(oldDefaultObject, oldDefaultObject);
		if (newDefaultObject) traverseBuildDefaultObjectData(newDefaultObject, newDefaultObject);

		for (const linkedObject of oldLinkedObjects)
		{
			if (!linkedObject) continue;
			traverseClearLinkedObjectData(linkedObject, linkedObject);
		}

		for (let i = 0; i < newLinkedObjects.length; i++)
		{
			const linkedObject = newLinkedObjects[i];

			if (!linkedObject) continue;
			paralleltraverseBuildLinkedObjectData(linkedObject, newDefaultObject!, linkedObject, linkedScenes[i]);
		}
	}

	findDefaultObject(defaultObjectGuid: string, defaultSceneGuid: string | undefined = undefined): THREE.Object3D | null
	{
		if (defaultSceneGuid)
		{
			const sceneRoot = Engine.getInstance().getSelectionRoot(defaultSceneGuid);
			const object = SceneObjectUtils.findObjectByGuid(sceneRoot, defaultObjectGuid);
			if (object)
			{
				return object;
			}
		}
		else
		{
			for (const sceneGuid of this.defaultSceneGuidToLinkedScenesMap.keys())
			{
				const sceneRoot = Engine.getInstance().getSelectionRoot(sceneGuid);
				const object = SceneObjectUtils.findObjectByGuid(sceneRoot, defaultObjectGuid);
				if (object)
				{
					return object;
				}
			}
		}

		return null;
	}

	isDefaultHierarchyRestorable(object: THREE.Object3D, scene: THREE.Scene = Engine.getInstance().getCurrentScene()!): boolean
	{
		if (!object.userData.unlinkedDefaultObjectGuid) return false;

		for (const defaultSceneGuid of this.defaultSceneGuidToLinkedScenesMap.keys())
		{
			//check if the default object already recieved a new linked object.
			if (this.defaultObjectAndSceneToLinkedObject.get(object.userData.unlinkedDefaultObjectGuid)?.get(scene.userData.guid)) return false;

			const sceneRoot = Engine.getInstance().getSelectionRoot(defaultSceneGuid);
			const unlinkedDefaultObject = SceneObjectUtils.findObjectByGuid(sceneRoot, object.userData.unlinkedDefaultObjectGuid) as THREE.Object3D;
			if (unlinkedDefaultObject)
			{
				const unlinkedDefaultObjectParent = unlinkedDefaultObject.parent!;
				const linkedParent = this.defaultObjectAndSceneToLinkedObject.get(unlinkedDefaultObjectParent.userData.guid)?.get(scene.userData.guid);
				if (!linkedParent) return false;
				return (!linkedParent.userData.removedFromScene && linkedParent.userData.defaultObjectGuid === unlinkedDefaultObjectParent.userData.guid);
			}
		}

		return false;
	}

	restoreDefaultHierarchy(object: THREE.Object3D, scene: THREE.Scene = Engine.getInstance().getCurrentScene()!): void
	{
		assert(object.userData.unlinkedDefaultObjectGuid);
		for (const defaultSceneGuid of this.defaultSceneGuidToLinkedScenesMap.keys())
		{
			const sceneRoot = Engine.getInstance().getSelectionRoot(defaultSceneGuid);
			const unlinkedChildDefaultObject = SceneObjectUtils.findObjectByGuid(sceneRoot, object.userData.unlinkedDefaultObjectGuid);
			if (unlinkedChildDefaultObject)
			{
				const unlinkedChildDefaultObjectParent = unlinkedChildDefaultObject.parent!;
				const linkedParent = this.defaultObjectAndSceneToLinkedObject.get(unlinkedChildDefaultObjectParent.userData.guid)?.get(scene.userData.guid);
				if ( linkedParent)
				{
					if (object.userData.markedSaveRoot && !unlinkedChildDefaultObject.userData.markedSaveRoot)
					{
						object.userData.originalParentGuid = linkedParent.userData.guid;
					}
					if (object.parent === linkedParent)
					{
						object.removeFromParent();
					}
					new SetObjectParentAction([object], linkedParent);
					ProjectSaver.getInstance().processUnlinkedObjects(object);
				}
				break;
			}
		}
	}

	isDefaultObject(objectGuid: string): boolean
	{
		return this.defaultObjectAndSceneToLinkedObject.has(objectGuid);
	}

	getDefaultObject(linkedObjectGuid: string): THREE.Object3D | null
	{
		return this.linkedObjectGuidToDefaultObjectMap.get(linkedObjectGuid) ?? null;
	}

	getLinkedObject(defaultObject: THREE.Object3D | string, linkedSceneGuid: string): THREE.Object3D | null
	{
		const objectGuid = typeof defaultObject === 'string' ? defaultObject : defaultObject.userData.guid;
		return this.defaultObjectAndSceneToLinkedObject.get(objectGuid)?.get(linkedSceneGuid) ?? null;
	}

	getLinkedScenes(defaultSceneGuid: string): THREE.Scene[]
	{
		return this.defaultSceneGuidToLinkedScenesMap.get(defaultSceneGuid) ?? [];
	}

	getLinkedScenesGuids(defaultSceneGuid: string): string[]
	{
		assert(this.defaultSceneGuidToLinkedScenesMap.has(defaultSceneGuid));
		const ret: string[] = [];
		for (const linkedScene of this.defaultSceneGuidToLinkedScenesMap.get(defaultSceneGuid) || [])
		{
			ret.push(linkedScene.userData.guid);
		}
		return ret;
	}

	getLinkedObjects(defaultObject: THREE.Object3D): THREE.Object3D[]
	{
		const linkedObjects: THREE.Object3D[] = [];
		const linkedSceneToObject = this.defaultObjectAndSceneToLinkedObject.get(defaultObject.userData.guid) as LinkedSceneToObject;
		if (!linkedSceneToObject)
		{
			return linkedObjects;
		}

		for (const linkedObject of linkedSceneToObject.values())
		{
			linkedObjects.push(linkedObject);
		}

		return linkedObjects;
	}

	isLinkedToDefaultObject(linkedObjectGuid: string, defaultObjectGuid: string): boolean
	{
		if (!this.defaultObjectAndSceneToLinkedObject.has(defaultObjectGuid)) return false;

		const linkedSceneToObject = this.defaultObjectAndSceneToLinkedObject.get(defaultObjectGuid) as LinkedSceneToObject;

		for (const linkedObject of linkedSceneToObject.values())
		{
			if (linkedObject.userData.guid === linkedObjectGuid) return true;
		}

		return false;
	}

	onObjectGuidReplaced(oldGuid: string, newGuid: string): void
	{
		if (this.linkedObjectGuidToDefaultObjectMap.has(oldGuid))
		{
			const defaultObject = this.linkedObjectGuidToDefaultObjectMap.get(oldGuid)!;
			this.linkedObjectGuidToDefaultObjectMap.delete(oldGuid);
			this.linkedObjectGuidToDefaultObjectMap.set(newGuid, defaultObject);
		}
	}

	isValidLinkedObject_old(objectParent: THREE.Object3D, sceneGuid: string, defaultObject: THREE.Object3D): boolean
	{
		if (this.defaultObjectAndSceneToLinkedObject.get(defaultObject.userData.guid)?.has(sceneGuid))
		{
			return false; //2 linked objects are pointing at the same default object? can't have that happen
		}
		if (objectParent)
		{
			if (!objectParent.userData.defaultObjectGuid && objectParent.type !== 'Scene')
			{
				return false;
			}
			if (objectParent.userData.defaultObjectGuid && objectParent.userData.defaultObjectGuid !== defaultObject.parent!.userData.guid)
			{
				return false;
			}
		}

		return true;
	}

	onLinkedObjectLoaded_old(object: THREE.Object3D, sceneGuid: string, defaultObject: THREE.Object3D | string): void
	{
		if (typeof defaultObject === 'string') defaultObject = this.findDefaultObject(defaultObject)!;

		assert(defaultObject);
		if (!this.isValidLinkedObject_old(object.parent!, sceneGuid, defaultObject)) return;

		this.linkedObjectGuidToDefaultObjectMap.set(object.userData.guid, defaultObject);
		this.defaultObjectAndSceneToLinkedObject.get(defaultObject.userData.guid)?.set(sceneGuid, object);
		object.userData.defaultObjectGuid = defaultObject.userData.guid;
	}

	onDefaultSceneLoaded_old(scene: THREE.Scene): void
	{
		scene.userData.isDefaultScene = true;
		this.defaultSceneGuidToLinkedScenesMap.set(scene.userData.guid, []);

		const sceneRoot = Engine.getInstance().getSelectionRoot(scene.userData.guid) as THREE.Object3D;
		sceneRoot.traverse((child) =>
		{
			if (child.userData.guid)
			{
				this.defaultObjectAndSceneToLinkedObject.set(child.userData.guid, new Map<string, THREE.Object3D>());
			}
		});
	}

	clearData(): void
	{
		this.defaultObjectAndSceneToLinkedObject.clear();
		this.defaultSceneGuidToLinkedScenesMap.clear();
		this.linkedObjectGuidToDefaultObjectMap.clear();
		this.defaultObjectsCopyMap.clear();
	}
}
